import WorkerChatConnection from './worker_chat_connection';
import consumer from '../../channels/consumer';

// The guided lesson phrase panel is a React island in its own pack, so it
// cannot be handed this instance directly. Transport news reaches it as DOM
// events on `document` — the bridge audio_player_service and
// chat_message_service already use — and `window.languaChatConnection` gives it
// the current transport at mount time, before any event has fired.
export const LESSON_EVENT_NAME = 'chat:lessonevent';
export const WORKER_STATE_EVENT_NAME = 'chat:workerstatechange';

export default class ChatConnectionAdapter {
  constructor(options) {
    this.chatId = options.chatId;
    this.chatOptions = options.chatOptions;
    this.userId = options.userId;
    this.railsApiUrl = options.railsApiUrl;
    this.callbacks = options.callbacks;
    this.workerReady = false;
    this.dedicatedWorker = document.getElementById('chat-form')?.dataset?.dedicatedChatWorker === 'true';

    window.languaChatConnection = this;

    // Main chat stays on the Worker; existing Rails jobs deliver supplementary
    // results over the same ActionCable subscription used by consumer chat.
    this.connectToActionCable();

    // Create Worker connection if enabled
    if (this.chatOptions.workerEnabled) {
      this.connectToWorker();
    }
  }
  
  connectToActionCable() {
    
    const that = this;
    this.subscription = consumer.subscriptions.create(
      { 
        channel: "ChatChannel", 
        chat_id: this.chatId 
      },
      {
        received: function(data) {
          if (that.dedicatedWorker && !['translation', 'correction_explanation', 'alternative_response'].includes(data?.type)) return;
          that.handleReceived(data);
        },
        connected: function() {
          if (!that.dedicatedWorker && that.callbacks.connected) {
            that.callbacks.connected();
          }
        },
        disconnected: function() {
          if (!that.dedicatedWorker && that.callbacks.disconnected) {
            that.callbacks.disconnected();
          }
        }
      }
    );
  }
  
  connectToWorker() {

    // Wrap the original callbacks to add notifications
    const originalCallbacks = this.callbacks;
    const enhancedCallbacks = {
      ...originalCallbacks,
      received: (data) => {
        this.handleReceived(data);
      },
      connected: () => {
        // Don't mark as ready yet - wait for context_initialized

        // Call original callback if exists
        if (!this.dedicatedWorker && originalCallbacks.connected) {
          originalCallbacks.connected();
        }
      },
      contextReady: () => {
        this.setWorkerReady(true);
        if (this.dedicatedWorker && originalCallbacks.connected) originalCallbacks.connected();
      },
      disconnected: () => {
        this.setWorkerReady(false);

        // Call original callback if exists
        if (originalCallbacks.disconnected) {
          originalCallbacks.disconnected();
        }
      }
    };
    
    this.workerConnection = new WorkerChatConnection({
      chatId: this.chatId,
      userId: this.userId,
      railsApiUrl: this.railsApiUrl,
      chatOptions: this.chatOptions,
      callbacks: enhancedCallbacks
    });
    
    this.workerConnection.connect();
  }
  
  // Single funnel for both transports. Lesson frames are handed to the phrase
  // panel and stop here: they carry no chat message, and the chat service's
  // received handler would fall through to its message branch and scroll the
  // transcript for them.
  handleReceived(data) {
    if (data && data.type === 'lesson_event') {
      // A lesson frame can only come from the Worker — ActionCable never sends
      // one — so its arrival is also proof the live transport is up.
      this.setWorkerReady(true);
      document.dispatchEvent(new CustomEvent(LESSON_EVENT_NAME, { detail: data }));
      return;
    }

    if (this.callbacks.received) {
      this.callbacks.received(data);
    }
  }

  setWorkerReady(ready) {
    if (this.workerReady === ready) return;

    this.workerReady = ready;
    document.dispatchEvent(new CustomEvent(WORKER_STATE_EVENT_NAME, {
      detail: { workerReady: this.isWorkerReady() }
    }));
  }

  sendMessage(message) {
    if (this.workerConnection && this.chatOptions.workerEnabled && this.workerReady) {
      // Send through Worker when enabled and ready
      this.workerConnection.sendMessage(message);
    } else {
      if (this.dedicatedWorker) {
        this.callbacks.received?.({ type: 'error', error: true, worker_error: true, message: 'Cartus chat is not ready. Please try again.' });
        return;
      }
      // Send through ActionCable
      this.subscription.perform('receive', message);
    }
  }

  isWorkerReady() {
    return this.chatOptions.workerEnabled && this.workerReady;
  }

  refreshContext() {
    if (this.workerConnection && this.chatOptions.workerEnabled && this.workerReady) {
      this.workerConnection.refreshContext();
    }
  }
  
  disconnect() {
    if (this.subscription) {
      consumer.subscriptions.remove(this.subscription);
    }
    
    if (this.workerConnection) {
      this.workerConnection.disconnect();
    }
  }
}
